import {createContext, Dispatch, ReactElement, SetStateAction, useEffect, useRef, useState} from "react";
import WsClient from "../ws/WsClient";
import {ConferenceUser, TranslationConferenceTokenData, UserFromToken} from "@inpriton/types";
import {ChatHistoryMessage, ConferenceAdmittedEvent, ConferenceStatusType} from "@inpriton/types/conferenceTypes";
import {jwtDecode} from "jwt-decode";
import {NotificationContextProvider} from "./NotificationContext";
import {MediaContextProvider} from "./MediaContext";
import useWebRtcClient from "../webrtc/WebRtcClient";
import {setPushServiceWorkerRegistration} from "../lib/pushNotification/PushNotificationService";
import type {ConferenceUserWithStatus, UserLiveStatus} from "@inpriton/types/conferenceTypes";
import VolumeMeter from '../helper/VolumeMeter'
import {useParams} from "react-router-dom";
import {WS_URL} from "../config";
import {ConsumeFailureNotifier, PresenceNotifier, RemovalNotifier} from "./app/notifiers";
import useRoster from "./app/useRoster";
import useRemoteStreams from "./app/useRemoteStreams";
import useChat, {ChatMessage} from "./app/useChat";
import {registerServiceWorker} from "./app/serviceWorker";
import {ArmoredKeyPair, loadOrGenerateKeys} from "./app/pgpKeys";
import {decryptMessage} from "../lib/pgp";
import {installViewportHeight} from "./app/viewportHeight";

export enum SidebarType {
  None = 'none',
  Participants = 'participants',
  Chat = 'chat',
  DeviceSettings = 'device-settings',
}

export type UserStream = {
  uuid: string
  deviceType: string
  stream: MediaStream
  trackIds: {
    [trackType: string]: string
  }
  hasAudio: boolean
  hasVideo: boolean
  volumeMeter: VolumeMeter|null
}

export type StreamsType = {
  [uuid: string]: {
    [deviceType: string]: UserStream
  }
}

const AppContext = createContext<{
  initialize: (token: string) => void,
  initialized: boolean, setInitialized: (initialized: boolean) => void,
  keys: ArmoredKeyPair|null, setKeys: (keys: ArmoredKeyPair|null) => void
  ws: WsClient|null, setWs: (ws: WsClient|null) => void
  currentRoom: string|null, setCurrentRoom: (uuid: string|null) => void
  checkedIn: boolean, setCheckedIn: (checkedIn: boolean) => void
  userData: UserFromToken|null, setUserData: (userData: UserFromToken) => void
  conferenceStatus: ConferenceStatusType, setConferenceStatus: Dispatch<SetStateAction<ConferenceStatusType>>
  messages: ChatMessage[]
  markRead: (id: string) => void
  onHistory: (rows: ChatHistoryMessage[], options: { reconnect: boolean, publicKey: string }) => void
  users: ConferenceUserWithStatus[], setUsers: (users: ConferenceUserWithStatus[]) => void
  typingUsers: string[], setTypingUsers: (uuids: string[]) => void
  sidebar: SidebarType, setSidebar: (type: SidebarType) => void
  streams: StreamsType, setStreams: (streams: StreamsType) => void, updateStreams: (uuid: string, status: UserLiveStatus, fromSnapshot?: boolean) => Promise<void>
  userVolumes: { [uuid: string]: number }, setUserVolumes: (volumes: { [uuid: string]: number }) => void
  // uuid -> display-name cache that survives roster removal, so chat history keeps sender names.
  userNames: { [uuid: string]: string }
  wsConnected: boolean
  // RISKS.md/P1.19: why we left, when a moderator action revoked our session — 'removed' (kicked)
  // or 'ended' (the whole meeting was ended); RISKS.md/P1.20 adds 'expired' (our session can no
  // longer be refreshed). Drives a terminal notice instead of the misleading "reconnecting"
  // overlay. null = still in the meeting.
  exitReason: 'removed' | 'ended' | 'expired' | null
}>({
  initialize: () => null,
  initialized: false,
  setInitialized: () => null,
  keys: null,
  setKeys: () => null,
  ws: null,
  setWs: () => null,
  currentRoom: '',
  setCurrentRoom: () => null,
  checkedIn: false,
  setCheckedIn: () => null,
  userData: null, setUserData: () => null,
  conferenceStatus: { started: null, mayStart: false, users: [], admitted: null, conferenceLanguages: [], userPreferences: null, translationType: null },
  setConferenceStatus: () => null,
  messages: [], markRead: () => null, onHistory: () => null,
  users: [], setUsers: () => null,
  typingUsers: [], setTypingUsers: () => null,
  sidebar: SidebarType.None, setSidebar: () => null,
  streams: {}, setStreams: () => null, updateStreams: async () => {},
  userVolumes: {}, setUserVolumes: () => null,
  userNames: {},
  wsConnected: false,
  exitReason: null,
})

const AppContextProvider = (props: { children: ReactElement }) => {
  const webrtc = useWebRtcClient()

  const [ initialized, setInitialized ] = useState(false)
  const [ keys, setKeys ] = useState<ArmoredKeyPair|null>(null)
  const [ ws, setWs ] = useState<WsClient|null>(null)
  const [ currentRoom, setCurrentRoom ] = useState<string|null>(localStorage.getItem('current-room'))
  const [ checkedIn, setCheckedIn ] = useState<boolean>(false)
  const [ userData, setUserData ] = useState<UserFromToken|null>(null)

  // Start with NO sidebar open — the meeting room is shown alone for both host and users. The
  // participant sidebar opens only on demand (the control-bar button, or the admit toast's second
  // action), not automatically on entering / being admitted.
  const [ sidebar, setSidebar ] = useState<SidebarType>(SidebarType.None)

  const [ wsConnected, setWsConnected ] = useState(false)
  const [ exitReason, setExitReason ] = useState<'removed' | 'ended' | 'expired' | null>(null)

  // RISKS.md/P2.58: a ref, not `userData`, so the guard holds whatever render a caller's closure
  // came from — `userData` is correct today only because it commits in the same batch as `ws`.
  const selfUuidRef = useRef<string | null>(null)

  // RISKS.md/P2.2: pass context values in. `useContext` in a hook called here returns the defaults
  // (`ws: null`, no-op toasts): this is AppContext's provider, and NotificationContext's is below it.
  const roster = useRoster()
  const remote = useRemoteStreams(webrtc, selfUuidRef, roster.usersRef)

  // RISKS.md/P1.32: not in the service worker, which loses the key whenever the browser stops it
  // while idle. A ref for the reason selfUuidRef gives.
  const keysRef = useRef<ArmoredKeyPair | null>(null)
  const chat = useChat(async encrypted => {
    if (!keysRef.current) {
      throw new Error('no chat key')
    }
    return decryptMessage(encrypted, keysRef.current.privateKey)
  })

  // RISKS.md/P1.6: the provider outlives the meeting room (it sits above the router), so a rejoin
  // re-runs initialize(). Undo the previous run first, and on the provider's own unmount.
  const teardownRef = useRef<(() => void) | null>(null)
  useEffect(() => () => {
    teardownRef.current?.()
    teardownRef.current = null
  }, [])

  const initialize = async (token: string) => {
    teardownRef.current?.()
    const cleanups: Array<() => void> = [remote.clearStreams, () => webrtc.reset()]
    teardownRef.current = () => cleanups.splice(0).forEach(fn => fn())
    // RISKS.md/P1.19: clear any prior "you were removed / session ended" state — re-initializing
    // (e.g. clicking OK on that notice → green room → rejoin) starts a fresh session, so the
    // terminal overlay must not persist into the rejoined meeting.
    setExitReason(null)
    // RISKS.md/P2.78: onHistory keeps messages it isn't served, so a rejoin after End would still
    // show the deleted chat.
    chat.clear()
    const tokenData = jwtDecode(token) as TranslationConferenceTokenData
    selfUuidRef.current = tokenData.user.uuid
    const keys = await loadOrGenerateKeys(tokenData.user.uuid)

    keysRef.current = keys
    setKeys(keys)

    const registration = await registerServiceWorker()

    // RISKS.md/P2.35: handed over because a bare `getRegistration()` cannot find it.
    setPushServiceWorkerRegistration(registration)

    const ws = await new Promise<WsClient>(async (resolve) => {
      const ws = new WsClient(WS_URL, 'inpriton')
      resolve(ws)
    })
    // RISKS.md/P2.77: on the provider's own unmount (the page's error screen) no App is left to
    // close it, and the green room's join chain would go on publishing over it.
    cleanups.push(() => ws.disconnect())

    ws.onStateChange(connected => {
      setWsConnected(connected)
      // RISKS.md/P1.13 + P1.22: a transient drop runs no teardown, so nothing else clears the dead
      // cache or the entries — and reset() leaves each entry's `trackIds`, so the re-join would find
      // every id unchanged and consume nothing. ⚠ reset() FIRST: otherwise disposal's closeStream
      // queues its teardown onto the socket that replaces this one.
      if (!connected) {
        webrtc.reset()
        remote.clearStreams()
      }
    })

    // RISKS.md/P1.20: silent refresh gave up for good (revoked session, rejected refresh, or a
    // token we can't refresh from). Previously it just stopped, leaving the user in a meeting
    // whose credentials were already dead — every later REST call 401ing with no explanation.
    // Same teardown as the `removed` / `session-ended` pushes below: stop reconnecting and show
    // a terminal notice.
    ws.onSessionDead(reason => {
      // RISKS.md/P1.22: a kick must never surface as 'expired', whose copy tells the user to rejoin
      // a room they are banned from. Either branch can carry it: this rejection, or an earlier push.
      setExitReason(prev => reason === 'removed' ? 'removed' : (prev ?? 'expired'))
      ws.disconnect()
    })

    setWs(ws)

    ws.setToken(token)
    setUserData(tokenData.user)
    setCurrentRoom(tokenData.conference)

    setInitialized(true)

    cleanups.push(installViewportHeight())
  }

  useEffect(() => {
    if (!checkedIn || !ws || !keys) {
      return
    }

    ws.onMessage(async (data) => {
      if (data.action === 'ping') {
        // RISKS.md/P1.3: pong gets no reply (the server just stamps lastOnline) — fire-and-forget,
        // or every elapsed ping would leave a pending request that rejects at disconnect.
        ws.send({ action: 'pong' }, false)
        return
      }
      if (data.action === 'room:action') {
        if (data.kind === 'typing') {
          chat.onTyping(data)
        }
      } else if (data.action === 'admitted') {
        const admitted = data as ConferenceAdmittedEvent
        roster.setConferenceStatus(admitted.conference)
        roster.setUsers(admitted.conference.users)
        chat.onHistory(admitted.history ?? [], { reconnect: false, publicKey: keys.publicKey })

        admitted.conference.users.forEach(user => {
          remote.updateStreams(user.uuid, user.status, true)
        })

      } else if (data.action === 'removed') {
        // RISKS.md/P1.19: a moderator removed us. Our session is revoked server-side (we
        // can't refresh or re-join), so stop auto-reconnecting — media drops immediately —
        // and flag the exit so the UI shows a terminal notice instead of the misleading
        // "reconnecting" overlay that a plain disconnect would trigger.
        setExitReason('removed')
        ws.disconnect()
      } else if (data.action === 'session-ended') {
        // RISKS.md/P1.19: the moderator ended the meeting for everyone (conference:end). Same
        // teardown as `removed`, but a distinct notice ("the organizer ended the session").
        setExitReason('ended')
        ws.disconnect()
        chat.clear()
      } else if (data.action === 'user:update') {
        roster.onUserUpdate(data.user)
      } else if (data.action === 'user:removed') {
        roster.onUserRemoved(data.uuid)
        void remote.removeStreams(data.uuid)
      } else if (data.action === 'preferences:updated') {
        roster.onPreferencesUpdated(data)
      } else if (data.action === 'user:status') {
        const { uuid, status }: { uuid: string, status: UserLiveStatus } = data
        roster.onUserStatus(uuid, status)
        await remote.updateStreams(uuid, status)
      } else {
        chat.onChatMessage(data)
      }
    })
    // Re-bind on `ws` too: a rejoin (Leave / window-close / End-Session) creates a NEW WsClient,
    // but `checkedIn` is already true from the prior session, so depending on `checkedIn` alone
    // never re-ran this effect — the new client had no onMessage handler and silently dropped every
    // server message (`admitted`, `user:update`, …). That broke admit-on-rejoin. (RISKS.md/P1.13.)
  }, [checkedIn, ws]);

  useEffect(() => {
    localStorage.setItem('current-room', currentRoom || '')
  }, [currentRoom]);

  const value = {
    initialize,
    initialized, setInitialized,
    keys, setKeys,
    ws, setWs,
    currentRoom, setCurrentRoom,
    checkedIn, setCheckedIn,
    userData, setUserData,
    conferenceStatus: roster.conferenceStatus, setConferenceStatus: roster.setConferenceStatus,
    messages: chat.messages, markRead: chat.markRead, onHistory: chat.onHistory,
    users: roster.users, setUsers: roster.setUsers,
    typingUsers: chat.typingUsers, setTypingUsers: chat.setTypingUsers,
    sidebar, setSidebar,
    streams: remote.streams, setStreams: remote.commitStreams, updateStreams: remote.updateStreams,
    userVolumes: roster.userVolumes, setUserVolumes: roster.setUserVolumes,
    userNames: roster.userNames,
    wsConnected,
    exitReason,
  }

  return <AppContext.Provider value={value}>
    <NotificationContextProvider>
      <>
        <RemovalNotifier />
        <PresenceNotifier />
        <ConsumeFailureNotifier failed={remote.streamsFailed} onRetry={remote.retryStreams} />
        <MediaContextProvider>
          {props.children}
        </MediaContextProvider>
      </>
    </NotificationContextProvider>
  </AppContext.Provider>
}

export {
  AppContext as default,
  AppContextProvider,
}